import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject, Injector } from '@angular/core';
import { environment } from '@environments/environment';
import { PageI } from '@interfaces/page.interface';
import { PauseI } from '@interfaces/pauses.interface';
import { TeamsI } from '@interfaces/teams.interface';
import { Observable, map } from 'rxjs';
import { UserI } from '@interfaces/user.interface';
import { BehaviorSubject, filter, firstValueFrom, of, from } from 'rxjs';
import { TeamsSocketService } from '@events/teams/teams-socket.service';
import { ChangeOperationCollectionEventI } from '@interfaces/change-operation-collection-event.interface';
import { ApiResponseI } from '@interfaces/api-response.interface';
import { cloneDeep } from 'lodash';
import { UserHelperService } from '@helpers/user-helper.service';

@Injectable({
  providedIn: 'root',
})
export class TeamsService {
  private baseUrl = environment.OMNISMART_API_TEAM;
  private slug_url = 'teams/';

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(private http: HttpClient, private injector: Injector) {}

  public initOnce() {
    const teamsSocketService = this.injector.get(TeamsSocketService);
    if (!teamsSocketService) {
      return;
    }
    teamsSocketService.init();
    teamsSocketService.change$.subscribe((change) => {
      if (!change) {
        return;
      }
      this.treatCrudOperations(change);
    });
  }

  private async treatCrudOperations(change: ChangeOperationCollectionEventI<TeamsI>) {
    let teams: TeamsI[] | null = await firstValueFrom(this.teamsCache$);
    if (!teams) {
      //se esta null n precida alimentar o cache , pois será carregado por completo quando usado
      return;
    }
    const { operation, data } = change;
    if (!data) {
      return;
    }
    if (operation === 'delete') {
      const { _id } = data;
      const idx = teams.findIndex((row) => row._id === _id);
      if (idx !== -1) {
        teams.splice(idx, 1);
        this.teamsSubject.next(teams);
      }
    } else if (operation === 'update') {
      const { _id } = data;
      const idx = teams.findIndex((row) => row._id === _id);
      const isMember = this.isCurrentUserMember(data as TeamsI);
      if (idx !== -1 && isMember) {
        teams[idx] = data as TeamsI;
        this.teamsSubject.next(teams);
      } else if (idx !== -1 && !isMember) {
        //usuario logado deixou de pertencer a equipe → remove do cache
        teams.splice(idx, 1);
        this.teamsSubject.next(teams);
      } else if (idx === -1 && isMember) {
        //usuario logado passou a pertencer a equipe → adiciona ao cache
        teams.push(data as TeamsI);
        this.teamsSubject.next(teams);
      }
    } else if (operation === 'create') {
      teams.push(data as TeamsI);
      this.teamsSubject.next(teams);
    }
  }

  public createTeam(team: TeamsI): Observable<TeamsI> {
    return this.http.post<TeamsI>(this.baseUrl + this.slug_url, team);
  }

  public getTeamsByCustomerId(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 999,
    active = 'createdAt'
  ): Observable<PageI<TeamsI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<TeamsI>>(this.baseUrl + this.slug_url, {
      params,
    });
  }

  public getAllTeams(params?: { sort?: string; active?: string }): Observable<PageI<TeamsI>> {
    return this.http.get<PageI<TeamsI>>(this.baseUrl + this.slug_url, {
      params: {
        page: 1,
        limit: 999,
        ...params,
      },
    });
  }

  private teamsSubject: BehaviorSubject<TeamsI[] | null> = new BehaviorSubject<TeamsI[] | null>(null);

  private get teamsCache$() {
    return this.teamsSubject.asObservable();
  }
  public getTeamByUserId(options?: { includesDefault?: boolean }): Observable<TeamsI[]> {
    const promise = async () => {
      const cachedTeams = await firstValueFrom(this.teamsCache$);
      const requestValue: Observable<TeamsI[]> = this.http
        .get<TeamsI[]>(this.baseUrl + this.slug_url + 'user', {
          params: {
            includesDefault: options?.includesDefault ? 'true' : 'false',
          },
        })
        .pipe(
          map((result) => {
            return result.sort((first, second) => (first.name > second.name ? 1 : second.name < first.name ? -1 : 0));
          })
        );
      if (cachedTeams === null) {
        //unloaded
        const teams = await firstValueFrom<TeamsI[]>(requestValue);
        this.teamsSubject.next(teams);
        return cloneDeep(teams ?? []);
      }
      return cloneDeep(cachedTeams ?? []);
    };
    return from(promise());
  }

  public getTeamsByCurrentUserProfileLevel(options?: { includesDefault?: boolean }): Observable<TeamsI[]> {
    return this.getTeamByUserId(options);
  }

  private isCurrentUserMember(team: TeamsI): boolean {
    const userHelper = this.injector.get(UserHelperService);
    const toIds = (list?: UserI[] | string[]): string[] =>
      ((list ?? []) as Array<UserI | string>)
        .map((member) => (typeof member === 'string' ? member : member?._id))
        .filter(Boolean) as string[];
    const memberIds = [...toIds(team.agents), ...toIds(team.supervisors)];
    return memberIds.some((id) => userHelper.verifyIfUserIsCurrentUser(id));
  }

  public getTeamById(teamId: string): Observable<TeamsI> {
    return this.http.get<TeamsI>(this.baseUrl + this.slug_url + teamId);
  }

  public getTeamByPabxQueue(pabxQueue: string): Observable<TeamsI> {
    return this.http.get<TeamsI>(this.baseUrl + this.slug_url + 'pabx-queue/' + pabxQueue);
  }

  public getPauses(): Observable<PauseI[]> {
    return this.http.get<PauseI[]>(this.baseUrl + this.slug_url + 'pauses');
  }

  public getPausesByUserId(userId: string): Observable<PauseI[]> {
    return this.http.get<PauseI[]>(this.baseUrl + this.slug_url + 'pauses-by-user/' + userId);
  }

  public getTeamDefaultByCustomer(customer: string): Observable<TeamsI> {
    const url = `${this.baseUrl}${this.slug_url}find-team-default/${customer}`;
    return this.http.get<TeamsI>(url);
  }

  public updateTeam(
    id: string,
    team: Partial<TeamsI>,
    options: { force: boolean } = { force: false }
  ): Observable<ApiResponseI<TeamsI>> {
    return this.http.patch<ApiResponseI<TeamsI>>(this.baseUrl + this.slug_url + id, team, {
      params: {
        force: options?.force ? 'true' : 'false',
      },
    });
  }

  public deleteTeam(id: string, options: { force: boolean } = { force: false }): Observable<any> {
    return this.http.delete(this.baseUrl + this.slug_url + id, {
      params: {
        force: options?.force ? 'true' : 'false',
      },
    });
  }

  public getCountTeams(): Observable<number> {
    return this.http.get<number>(this.baseUrl + this.slug_url + 'count');
  }

  public findTeamsBySupervisor(userId): Observable<{ teams: TeamsI[]; users: UserI[] }> {
    return this.http.get<{ teams: TeamsI[]; users: UserI[] }>(this.baseUrl + this.slug_url + 'supervisor/' + userId);
  }

  public postTeams(team: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + this.slug_url, team, this.httpOptions);
  }
}
